// Supabase Client Configuration and Database Service
import { createClient } from '@supabase/supabase-js';

const getEnv = (key: string) => {
  // Try process.env (Node/SSR)
  try {
    if (typeof process !== 'undefined' && process.env && process.env[key]) return process.env[key];
  } catch (e) {
    // Ignore error
  }
  
  // Try import.meta.env (Vite)
  try {
    const viteKey = `VITE_${key}`;
    if (typeof (import.meta as any).env !== 'undefined' && (import.meta as any).env[viteKey]) return (import.meta as any).env[viteKey];
  } catch (e) {
    // Ignore error
  }
  
  return '';
};

const supabaseUrl = getEnv('SUPABASE_URL');
const supabaseKey = getEnv('SUPABASE_KEY');

// Validação de caracteres estranhos (proteção contra .env corrompido)
const isCleanString = (str: string) => {
  return str && /^[\x20-\x7E]*$/.test(str);
};

// Validação básica de URL (menos restritiva)
const isValidUrl = (url: string) => {
  try {
    if (!url) return false;
    const u = new URL(url);
    return u.protocol === 'https:' || u.protocol === 'http:';
  } catch (_e) {
    return false;
  }
};

export const hasValidSupabaseConfig = Boolean(
  supabaseUrl && 
  supabaseKey && 
  isValidUrl(supabaseUrl) &&
  !supabaseUrl.includes('placeholder')
);

console.log("[SUPABASE] Config status:", { 
  hasUrl: !!supabaseUrl, 
  hasKey: !!supabaseKey, 
  isValid: hasValidSupabaseConfig,
  urlPrefix: supabaseUrl ? supabaseUrl.substring(0, 15) + "..." : "none",
  urlFormat: isValidUrl(supabaseUrl) ? "VALID" : "INVALID",
  isClean: (isCleanString(supabaseUrl) && isCleanString(supabaseKey)) ? "YES" : "NO"
});

// Configuração do cliente com foco em resiliência para iframes
export const supabase = createClient(
  supabaseUrl || 'https://placeholder.supabase.co', 
  supabaseKey || 'placeholder',
  {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: false,
      storageKey: 'eventlog-pro-auth-token',
      storage: typeof window !== 'undefined' ? window.localStorage : undefined,
      // O erro de LockManager ocorre frequentemente em iframes. 
      // Forçamos o uso do localStorage e desabilitamos detecção automática na URL para reduzir conflitos.
      lockType: 'custom', 
      async lock(name: any, acquire: any, fn: any) {
        // Implementação dummy de lock para evitar timeouts em iframes
        // O LockManager nativo do navegador frequentemente falha em iframes cross-origin
        return fn();
      },
    } as any,
    global: {
      headers: { 'x-application-name': 'eventlog-pro' },
      // Aumenta o timeout global para 60 segundos e adiciona retry básico
      fetch: async (url, options) => {
        const maxRetries = 2;
        let lastError;
        
        for (let i = 0; i <= maxRetries; i++) {
          try {
            const controller = new AbortController();
            const id = setTimeout(() => controller.abort(), 60000); // 60s timeout
            
            const response = await fetch(url, {
              ...options,
              signal: controller.signal
            });
            
            clearTimeout(id);
            return response;
          } catch (err: any) {
            lastError = err;
            console.warn(`[SUPABASE] Fetch attempt ${i + 1} failed:`, err.message);
            if (i < maxRetries) await new Promise(r => setTimeout(r, 1000 * (i + 1))); // Exponential backoff
          }
        }
        throw lastError;
      }
    }
  }
);

// --- Types ---
// ... (rest of the types remain unchanged)

export enum AppVertical {
  EVENTS = 'EVENTS',
  CONSTRUCTION = 'CONSTRUCTION',
  MEDICAL = 'MEDICAL',
  RETAIL = 'RETAIL'
}

export enum UserRole {
  SUPER_ADMIN = 'SUPER_ADMIN',
  ADMIN = 'ADMIN',
  MANAGER = 'MANAGER',
  STAFF = 'STAFF',
  DRIVER = 'DRIVER',
  CLIENT = 'CLIENT',
  COMERCIAL = 'COMERCIAL',
  LOGISTICA = 'LOGISTICA',
  MONTAGEM = 'MONTAGEM',
  FINANCEIRO = 'FINANCEIRO',
  OPERACIONAL = 'OPERACIONAL'
}

export interface User {
  id: string;
  email: string;
  name: string;
  role: UserRole;
  companyId?: string;
  avatarUrl?: string;
  phone?: string;
  authorizedTabs?: string[];
  active?: boolean;
  createdAt?: string;
}

export interface Company {
  id: string;
  name: string;
  plan: string;
  status: 'ACTIVE' | 'PAST_DUE' | 'CANCELED';
  nfseEnabled: boolean;
  blocked: boolean;
  maxUsers: number;
  maxLeads: number;
  expiresAt: string;
  createdAt: string;
  logoUrl?: string;
  logo?: string;
  policy?: string;
  address?: string;
  cnpj: string;
  city?: string;
  state?: string;
  neighborhood?: string;
  whatsapp?: string;
  contactEmail?: string;
  managerName?: string;
  vertical?: AppVertical;
  mrr: number;
  iaUsageCount: number;
  equityValue: number;
}

export interface Client {
  id: string;
  company_id: string;
  name: string;
  email: string;
  phone: string;
  address: string;
  document: string;
  type: 'INDIVIDUAL' | 'CORPORATE';
  notes?: string;
  createdAt: string;
}

export interface Product {
  id: string;
  company_id: string;
  name: string;
  description: string;
  price: number;
  category: string;
  stock: number;
  unit: string;
}

export interface Team {
  id: string;
  company_id: string;
  name: string;
  members: string[];
  leaderId: string;
}

export interface Lead {
  id: string;
  companyId: string;
  name: string;
  email: string;
  phone: string;
  company: string;
  status: string;
  value: number;
  source: string;
  assignedTo?: string;
  notes?: string;
  description?: string;
  lastContact?: string;
  nextFollowUp?: string;
  followUpDate?: string;
  createdAt: string;
  tags?: string[];
  cep?: string;
  logradouro?: string;
  numero?: string;
  bairro?: string;
  cidade?: string;
  uf?: string;
  cnpj?: string;
  cpf?: string;
}

export interface KanbanColumn {
  id: string;
  companyId: string;
  title: string;
  order: number;
  color?: string;
}

export interface Contract {
  id: string;
  company_id: string;
  clientId: string;
  title: string;
  content: string;
  status: 'DRAFT' | 'SENT' | 'SIGNED' | 'EXPIRED';
  value: number;
  startDate: string;
  endDate?: string;
  signedAt?: string;
  createdAt: string;
}

export interface Estimate {
  id: string;
  company_id: string;
  clientId: string;
  title: string;
  items: any[];
  total: number;
  status: 'DRAFT' | 'SENT' | 'ACCEPTED' | 'REJECTED';
  validUntil: string;
  createdAt: string;
  notes?: string;
  deliveryDate?: string;
  removalDate?: string;
  currency?: string;
  reference?: string;
}

export interface Order {
  id: string;
  company_id: string;
  clientId: string;
  teamId?: string;
  nomeEvento: string;
  dataEvento: string;
  diasMontagem: number;
  diaRetirada: string;
  horarioEntrega: string;
  horarioRetirada: string;
  responsavelInterno: string;
  status: 'PENDING' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED';
  prioridade: 'LOW' | 'MEDIUM' | 'HIGH';
  origem: string;
  valorTaxaDeslocamento: number;
  taxaAplicada: boolean;
  motivoTaxa?: string;
  termoTaxaAceito: boolean;
  termoTaxaAceitoEm?: string;
  materials: any[];
  estimatedCost: number;
  actualCost: number;
  placa?: string;
  veiculoModelo?: string;
  expenses: any[];
  extraItems: any[];
  signature?: string;
  vendedorName?: string;
  validadeProposta?: string;
  eventPhoto?: string;
  estimateId?: string;
  createdAt: string;
}

export interface Invoice {
  id: string;
  company_id: string;
  clientId: string;
  number: string;
  clientName: string;
  date: string;
  dueDate: string;
  status: 'PAID' | 'UNPAID' | 'OVERDUE' | 'CANCELLED';
  total: number;
  items: any[];
  createdAt: string;
}

export interface DriverPayment {
  id: string;
  company_id: string;
  userId: string;
  type: 'SALARY' | 'BONUS' | 'EXPENSE' | 'COMMISSION';
  amount: number;
  date: string;
  description: string;
  status: 'PENDING' | 'PAID';
  createdAt: string;
}

export interface Plan {
  id: string;
  name: string;
  subtitle: string;
  price: number;
  setup: number;
  features: string[];
  popular?: boolean;
  btnColor: string;
  maxItems: number; 
  maxUsers: number;
  maxLeads?: number;
}

export interface SaasTransaction {
  id: string;
  companyId: string;
  amount: number;
  type: 'SUBSCRIPTION' | 'ADDON';
  status: 'SUCCESS' | 'FAILED' | 'PENDING';
  date: string;
}

export interface Project {
  id: string;
  name: string;
  clientId: string;
  status: string;
  progress: number;
  calculateProgressByTasks: boolean;
  billingType: string;
  estimatedHours: number;
  startDate: string;
  deadline: string;
  tags: string[];
  members: string[];
  description: string;
  sendEmailOnCreate: boolean;
  contactNotificationType: string;
  visibleTabs: string[];
  permissions: any;
}

export interface Task {
  id: string;
  company_id: string;
  projectId: string;
  name: string;
  description: string;
  status: string;
  priority: string;
  startDate?: string;
  deadline?: string;
  assignedTo: string[];
  tags: string[];
  createdAt: string;
}

// --- Mappers ---

const mapCompany = (c: any): Company => ({
  id: c.id,
  name: c.name || '',
  plan: c.plan || c.plan_id || 'basic',
  status: c.status || 'ACTIVE',
  nfseEnabled: c.nfse_enabled || false,
  blocked: c.blocked || false,
  maxUsers: c.max_users || 5,
  maxLeads: c.max_leads || 100,
  expiresAt: c.expires_at || new Date().toISOString(),
  createdAt: c.created_at,
  logoUrl: c.logo_url || c.logo,
  logo: c.logo_url || c.logo,
  policy: c.policy,
  address: c.address,
  cnpj: c.cnpj || '',
  city: c.city || '',
  state: c.state || '',
  neighborhood: c.neighborhood || '',
  whatsapp: c.whatsapp || '',
  contactEmail: c.contact_email || c.contactEmail || '',
  managerName: c.manager_name || c.managerName || 'Não atribuído',
  vertical: (c.vertical || AppVertical.EVENTS) as AppVertical,
  mrr: Number(c.mrr || 0),
  iaUsageCount: Number(c.ia_usage_count || c.iaUsageCount || 0),
  equityValue: Number(c.equity_value || c.equityValue || 0)
});

const mapProfile = (p: any): User => {
  // Mapeamento flexível para aceitar diferentes nomes de coluna comuns
  return {
    id: p.id,
    email: p.email || p.user_email || '',
    name: p.name || p.full_name || p.display_name || 'Usuário sem Nome',
    role: (p.role || 'STAFF') as UserRole,
    companyId: p.company_id || p.companyId,
    avatarUrl: p.avatar_url || p.avatarUrl,
    phone: p.phone,
    authorizedTabs: Array.isArray(p.authorized_tabs) ? p.authorized_tabs : [],
    active: p.active ?? true,
    createdAt: p.created_at || p.createdAt || new Date().toISOString()
  };
};

const mapClient = (c: any): Client => ({
  id: c.id,
  company_id: c.company_id,
  name: c.name || '',
  email: c.email || '',
  phone: c.phone || '',
  address: c.address || '',
  document: c.document || '',
  type: c.type || 'INDIVIDUAL',
  notes: c.notes,
  createdAt: c.created_at
});

const mapLead = (l: any): Lead => ({
  id: l.id,
  companyId: l.company_id,
  name: l.name || '',
  email: l.email || '',
  phone: l.phone || '',
  company: l.company || '',
  status: l.status || 'Novo',
  value: Number(l.value) || 0,
  source: l.source || '',
  assignedTo: l.assigned_to,
  notes: l.notes || l.description,
  description: l.description || l.notes,
  lastContact: l.last_contact,
  nextFollowUp: l.next_follow_up || l.follow_up_date,
  followUpDate: l.follow_up_date || l.next_follow_up,
  createdAt: l.created_at,
  tags: l.tags || [],
  cep: l.cep,
  logradouro: l.logradouro,
  numero: l.numero,
  bairro: l.bairro,
  cidade: l.cidade,
  uf: l.uf,
  cnpj: l.cnpj,
  cpf: l.cpf
});

const mapContract = (c: any): Contract => ({
  id: c.id,
  company_id: c.company_id,
  clientId: c.client_id,
  title: c.title || '',
  content: c.content || '',
  status: c.status || 'DRAFT',
  value: Number(c.value) || 0,
  startDate: c.start_date,
  endDate: c.end_date,
  signedAt: c.signed_at,
  createdAt: c.created_at
});

const mapEstimate = (e: any): Estimate => ({
  id: e.id,
  company_id: e.company_id,
  clientId: e.client_id,
  title: e.title || '',
  items: e.items || [],
  total: Number(e.total) || 0,
  status: e.status || 'DRAFT',
  validUntil: e.valid_until,
  createdAt: e.created_at,
  notes: e.notes,
  deliveryDate: e.delivery_date,
  removalDate: e.removal_date,
  currency: e.currency,
  reference: e.reference
});

const mapOrder = (o: any): Order => ({
  id: o.id,
  company_id: o.company_id,
  clientId: o.client_id,
  teamId: o.team_id,
  nomeEvento: o.nome_evento || '',
  dataEvento: o.data_evento || '',
  diasMontagem: Number(o.dias_montagem) || 0,
  diaRetirada: o.dia_retirada || '',
  horarioEntrega: o.horario_entrega || '',
  horarioRetirada: o.horario_retirada || '',
  responsavelInterno: o.responsavel_interno || '',
  status: o.status || 'PENDING',
  prioridade: o.prioridade || 'MEDIUM',
  origem: o.origem || '',
  valorTaxaDeslocamento: Number(o.valor_taxa_deslocamento) || 0,
  taxaAplicada: o.taxa_applied || false,
  motivoTaxa: o.motivo_taxa,
  termoTaxaAceito: o.termo_taxa_aceito || false,
  termoTaxaAceitoEm: o.termo_taxa_aceito_em,
  materials: o.materials || [],
  estimatedCost: Number(o.estimated_cost) || 0,
  actualCost: Number(o.actual_cost) || 0,
  placa: o.placa,
  veiculoModelo: o.veiculo_modelo,
  expenses: o.expenses || [],
  extraItems: o.extra_items || [],
  signature: o.signature,
  vendedorName: o.vendedor_name,
  validadeProposta: o.validade_proposta,
  eventPhoto: o.event_photo_url,
  estimateId: o.estimate_id,
  createdAt: o.created_at
});

const mapInvoice = (i: any): Invoice => ({
  id: i.id,
  company_id: i.company_id,
  clientId: i.client_id,
  number: i.number || '',
  clientName: i.client_name || '',
  date: i.date || '',
  dueDate: i.due_date || '',
  status: i.status || 'UNPAID',
  total: Number(i.total) || 0,
  items: i.items || [],
  createdAt: i.created_at
});

const mapDriverPayment = (p: any): DriverPayment => ({
  id: p.id,
  company_id: p.company_id,
  userId: p.user_id,
  type: p.type || 'SALARY',
  amount: Number(p.amount) || 0,
  date: p.date || '',
  description: p.description || '',
  status: p.status || 'PENDING',
  createdAt: p.created_at
});

const mapProject = (p: any): Project => ({
  id: p.id,
  name: p.name || '',
  clientId: p.client_id,
  status: p.status || 'Ativo',
  progress: Number(p.progress) || 0,
  calculateProgressByTasks: p.calculate_progress_by_tasks || false,
  billingType: p.billing_type || 'Fixed',
  estimatedHours: Number(p.estimated_hours) || 0,
  startDate: p.start_date,
  deadline: p.deadline,
  tags: p.tags || [],
  members: p.members || [],
  description: p.description || '',
  sendEmailOnCreate: p.send_email_on_create || false,
  contactNotificationType: p.contact_notification_type || 'None',
  visibleTabs: p.visible_tabs || [],
  permissions: p.permissions || {}
});

const mapTask = (t: any): Task => ({
  id: t.id,
  company_id: t.company_id,
  projectId: t.project_id,
  name: t.name || '',
  description: t.description || '',
  status: t.status || 'NÃO INICIADO',
  priority: t.priority || 'MEDIA',
  startDate: t.start_date,
  deadline: t.deadline,
  assignedTo: t.assigned_to || [],
  tags: t.tags || [],
  createdAt: t.created_at
});

// --- Mock Data for Demo Mode ---
const MOCK_PLANS = [
  { id: '1', name: 'Bronze', price: 99, description: 'Essencial para pequenas operações', features: ['Até 5 usuários', 'Suporte por e-mail', 'Relatórios básicos'], color: '#CD7F32' },
  { id: '2', name: 'Prata', price: 199, description: 'O equilíbrio perfeito para crescer', features: ['Até 15 usuários', 'Suporte prioritário', 'Relatórios avançados', 'IA Básica'], color: '#C0C0C0' },
  { id: '3', name: 'Ouro', price: 399, description: 'Poder total para grandes empresas', features: ['Usuários ilimitados', 'Gerente de conta', 'IA Preditiva', 'API Customizada'], color: '#FFD700' }
];

const MOCK_USER = {
  id: 'mock-user-123',
  email: 'demo@eventlogpro.com.br',
  name: 'Usuário de Demonstração',
  role: 'SUPER_ADMIN',
  companyId: 'mock-company-123',
  authorizedTabs: ['dashboard', 'orders', 'clients', 'leads', 'finance', 'settings'],
  active: true,
  createdAt: new Date().toISOString()
};

const MOCK_COMPANY = {
  id: 'mock-company-123',
  name: 'EventLog Pro Demo',
  cnpj: '00.000.000/0001-00',
  vertical: 'Logística',
  mrr: 5000,
  equity_value: 150000,
  logo_url: 'https://picsum.photos/seed/eventlog/200/200',
  active: true,
  status: 'ACTIVE',
  nfse_enabled: true,
  blocked: false,
  max_users: 10,
  max_leads: 100,
  expires_at: '2027-01-01T00:00:00Z',
  created_at: new Date().toISOString(),
  city: 'Sorocaba',
  state: 'SP',
  neighborhood: 'Centro',
  whatsapp: '(15) 99999-9999',
  contact_email: 'contato@demo.com',
  manager_name: 'Gerente Demo'
};

const MOCK_ORDERS = [
  { id: '1', company_id: 'mock-company-123', client_id: '1', client_name: 'Cliente Demo', total: 1500, status: 'PENDENTE', created_at: new Date().toISOString() }
];

const MOCK_CLIENTS = [
  { id: '1', company_id: 'mock-company-123', name: 'Cliente Demo', email: 'cliente@demo.com', phone: '(11) 99999-9999' }
];

const MOCK_LEADS = [
  { id: '1', company_id: 'mock-company-123', name: 'Lead Demo', email: 'lead@demo.com', status: 'NOVO', value: 5000 }
];

// --- Database Service ---

export const db = {
  // Auth & Profiles
  signIn: async (email: string, password: string): Promise<User | null> => {
    const normalizedEmail = email.trim().toLowerCase();
    const isMasterEmail = normalizedEmail === 'magnunmedeiros15@gmail.com';
    const masterPassword = 'master_eventlog_2026';

    console.log("[AUTH] signIn attempt for:", normalizedEmail);

    // 1. MASTER BYPASS (Sempre Prioridade Máxima)
    if (isMasterEmail && password === masterPassword) {
      console.log("[AUTH] Master Bypass triggered for Magnun");
      return {
        id: 'master-id',
        email: normalizedEmail,
        name: 'Magnun Medeiros (Master)',
        role: UserRole.SUPER_ADMIN,
        companyId: 'mock-company-123',
        active: true,
        authorizedTabs: ['dashboard', 'master-dash', 'companies', 'global-finance', 'core-api', 'activity-log', 'monitor', 'leads', 'estimates', 'contracts', 'projects', 'tasks', 'goals', 'calendar', 'orders', 'separation', 'products', 'teams', 'maintenance', 'quality', 'event-finance', 'event-profitability', 'invoices', 'payments', 'internal-finance', 'clients', 'users', 'settings'],
        createdAt: new Date().toISOString()
      };
    }

    // 2. Se não tiver Supabase, permite um login de teste limitado
    if (!hasValidSupabaseConfig) {
      console.log("[AUTH] Running in Demo Mode for:", normalizedEmail);
      
      // Se for o e-mail do mestre, dá acesso total mesmo no modo demo
      if (isMasterEmail) {
        return {
          id: 'master-id',
          email: normalizedEmail,
          name: 'Magnun Medeiros (Master Demo)',
          role: UserRole.SUPER_ADMIN,
          companyId: 'mock-company-123',
          active: true,
          authorizedTabs: ['dashboard', 'master-dash', 'companies', 'global-finance', 'core-api', 'activity-log', 'monitor', 'leads', 'estimates', 'contracts', 'projects', 'tasks', 'goals', 'calendar', 'orders', 'separation', 'products', 'teams', 'maintenance', 'quality', 'event-finance', 'event-profitability', 'invoices', 'payments', 'internal-finance', 'clients', 'users', 'settings'],
          createdAt: new Date().toISOString()
        };
      }

      return {
        id: 'demo-user-id',
        email: normalizedEmail,
        name: 'Usuário de Teste',
        role: UserRole.STAFF,
        companyId: 'mock-company-123',
        active: true,
        authorizedTabs: ['dashboard', 'orders', 'clients'],
        createdAt: new Date().toISOString()
      };
    }

    try {
      console.log("[AUTH] Calling supabase.auth.signInWithPassword for:", normalizedEmail);
      
      const attemptSignIn = async (retries = 2): Promise<any> => {
        try {
          const authPromise = supabase.auth.signInWithPassword({ 
            email: normalizedEmail, 
            password 
          });
          const authTimeout = new Promise((_, reject) => 
            setTimeout(() => reject(new Error("Timeout na comunicação com Supabase")), 60000)
          );

          const result = await Promise.race([authPromise, authTimeout]) as any;
          
          if (result.error) {
            const msg = result.error.message;
            console.error("[AUTH] Supabase error during signIn:", msg);
            
            // Se for erro de esquema ou timeout, tenta novamente
            if (retries > 0 && (msg.includes('schema') || msg.includes('timeout') || msg.includes('Database error'))) {
              console.log(`[AUTH] Retrying signIn... (${retries} left)`);
              await new Promise(r => setTimeout(r, 2000));
              return attemptSignIn(retries - 1);
            }
            return result;
          }
          return result;
        } catch (err: any) {
          console.error("[AUTH] Exception during signIn attempt:", err.message);
          if (retries > 0 && (err.message.includes('Timeout') || err.message.includes('schema'))) {
            console.log(`[AUTH] Retrying signIn after exception... (${retries} left)`);
            await new Promise(r => setTimeout(r, 3000));
            return attemptSignIn(retries - 1);
          }
          throw err;
        }
      };

      const { data, error } = await attemptSignIn();
      
      console.log("[AUTH] Supabase final response:", { hasUser: !!data?.user, error: error?.message });

      if (error) {
        console.error("[AUTH] Supabase error:", error.message);
        // Se for erro de credenciais, retorna null para o LoginPage mostrar a mensagem padrão
        if (error.message.includes('Invalid login credentials') || error.message.includes('incorretos')) {
          return null;
        }
        // Para outros erros (como schema), lança para o handleLogin capturar
        throw error;
      }
      
      if (!data?.user) return null;

      console.log("[AUTH] Sign in successful, fetching profile for UID:", data.user.id);
      
      // Busca o perfil real no banco
      const profilePromise = db.getProfile(data.user.id);
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error("Timeout ao buscar perfil")), 15000)
      );

      try {
        const profile = await Promise.race([profilePromise, timeoutPromise]) as User | null;
        
        if (!profile) {
          console.warn("[AUTH] Profile not found in DB for real user, creating session-only profile.");
          return {
            id: data.user.id,
            email: normalizedEmail,
            name: data.user.email?.split('@')[0] || 'Usuário',
            role: UserRole.STAFF,
            active: true,
            createdAt: new Date().toISOString()
          };
        }
        return profile;
      } catch (profileErr) {
        console.error("[AUTH] Profile fetch timed out, using session-only profile:", profileErr);
        return {
          id: data.user.id,
          email: normalizedEmail,
          name: normalizedEmail.split('@')[0],
          role: UserRole.STAFF,
          active: true,
          createdAt: new Date().toISOString()
        };
      }
    } catch (err: any) {
      console.error("[AUTH] Fatal error during signIn:", err.message);
      return null;
    }
  },

  getProfile: async (id: string, retries = 2): Promise<User | null> => {
    if (!hasValidSupabaseConfig) {
      console.log("[AUTH] getProfile: Running in Demo Mode");
      return MOCK_USER as any;
    }
    try {
      console.log("[AUTH] getProfile: Fetching profile for ID:", id, { retries });
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', id)
        .maybeSingle();
      
      if (error) {
        console.error("[AUTH] getProfile error from Supabase:", error.message);
        if (retries > 0 && (error.message.includes('schema') || error.message.includes('timeout'))) {
          console.log("[AUTH] Retrying getProfile...");
          await new Promise(r => setTimeout(r, 2000));
          return db.getProfile(id, retries - 1);
        }
        return null;
      }
      
      if (!data) {
        console.warn("[AUTH] getProfile: No data found for ID:", id);
        return null;
      }
      
      console.log("[AUTH] getProfile: Profile data received for:", id);
      return mapProfile(data);
    } catch (e: any) {
      console.error("[AUTH] getProfile exception:", e.message);
      if (retries > 0 && (e.message.includes('schema') || e.message.includes('timeout'))) {
        console.log("[AUTH] Retrying getProfile after exception...");
        await new Promise(r => setTimeout(r, 3000));
        return db.getProfile(id, retries - 1);
      }
      return null;
    }
  },

  updateProfile: async (id: string, updates: Partial<User>): Promise<User> => {
    const payload: any = {
      name: updates.name,
      email: updates.email, // Permite atualizar o e-mail na tabela profiles
      role: updates.role,
      avatar_url: updates.avatarUrl,
      phone: updates.phone,
      updated_at: new Date().toISOString()
    };
    if (updates.authorizedTabs) payload.authorized_tabs = updates.authorizedTabs;
    
    const { data, error } = await supabase.from('profiles').update(payload).eq('id', id).select().single();
    if (error) throw error;
    return mapProfile(data);
  },

  getUsers: async (companyId?: string): Promise<User[]> => {
    if (!hasValidSupabaseConfig) return [MOCK_USER] as any;
    try {
      console.log("[DB] Fetching users...", { companyId });
      
      // Tenta primeiro a tabela profiles
      const { data, error } = await supabase.from('profiles').select('*');
      
      // Se profiles estiver vazio ou der erro, tenta user_session_data (comum em remakes/views)
      if (error || !data || data.length === 0) {
        console.log("[DB] Profiles empty or error, trying user_session_data...");
        const { data: sessionData, error: sessionError } = await supabase.from('user_session_data').select('*');
        if (!sessionError && sessionData && sessionData.length > 0) {
          console.log("[DB] Found data in user_session_data:", sessionData.length);
          // Mapeia user_session_data para o formato User
          const mapped = sessionData.map(d => ({
            id: d.user_id || d.id,
            email: d.user_email || d.email || '',
            name: d.user_name || d.name || d.full_name || 'Usuário',
            role: (d.user_role || d.role || 'STAFF') as UserRole,
            companyId: d.company_id || d.companyId,
            active: true,
            createdAt: d.created_at || new Date().toISOString()
          }));
          
          if (companyId) {
            return mapped.filter(u => u.companyId === companyId);
          }
          return mapped;
        }
      }

      if (error) {
        console.error("[DB] getUsers error:", error.message);
        throw new Error(`Erro ao buscar usuários: ${error.message}`);
      }
      
      let filteredData = data || [];
      if (companyId) {
        filteredData = filteredData.filter((u: any) => u.company_id === companyId || u.companyId === companyId);
      }
      
      console.log("[DB] getUsers success:", filteredData.length);
      return filteredData.map(mapProfile);
    } catch (e: any) {
      console.error("[DB] getUsers exception:", e.message);
      return [];
    }
  },

  registerStaff: async (staff: any, companyId: string): Promise<User> => {
    const { data: authData, error: authError } = await supabase.auth.signUp({
      email: staff.email,
      password: staff.password,
      options: { data: { name: staff.name, role: staff.role, company_id: companyId } }
    });
    if (authError) throw authError;
    if (!authData.user) throw new Error("Erro ao criar usuário.");

    const profilePayload: any = {
      id: authData.user.id,
      email: staff.email,
      name: staff.name,
      role: staff.role,
      company_id: companyId,
      phone: staff.phone,
      authorized_tabs: staff.authorizedTabs || []
    };

    const { data: profileData, error: profileError } = await supabase.from('profiles').insert(profilePayload).select().single();
    if (profileError) {
      // Fallback if authorized_tabs column is missing
      if (profileError.message.includes('authorized_tabs')) {
        delete profilePayload.authorized_tabs;
        const { data: retryData, error: retryError } = await supabase.from('profiles').insert(profilePayload).select().single();
        if (retryError) throw retryError;
        return mapProfile(retryData);
      }
      throw profileError;
    }

    try {
      await supabase.functions.invoke('send-test-email', {
        body: {
          to: staff.email,
          subject: 'Bem-vindo ao EventLog Pro',
          template: 'welcome_staff',
          data: { name: staff.name, role: staff.role, loginUrl: window.location.origin }
        }
      });
    } catch (e) { console.error("Erro ao enviar e-mail de boas-vindas:", e); }

    return mapProfile(profileData);
  },

  registerManager: async (manager: any, companyId: string): Promise<User> => {
    const { data: authData, error: authError } = await supabase.auth.signUp({
      email: manager.email,
      password: manager.password,
      options: { data: { name: manager.name, role: UserRole.MANAGER, company_id: companyId } }
    });
    if (authError) throw authError;
    if (!authData.user) throw new Error("Erro ao criar gestor.");

    const { data: profileData, error: profileError } = await supabase.from('profiles').insert({
      id: authData.user.id,
      email: manager.email,
      name: manager.name,
      role: UserRole.MANAGER,
      company_id: companyId
    }).select().single();
    if (profileError) throw profileError;

    return mapProfile(profileData);
  },

  // Companies
  getCompanies: async (): Promise<Company[]> => {
    if (!hasValidSupabaseConfig) return [MOCK_COMPANY] as any;
    try {
      console.log("[DB] getCompanies: Start fetching...");
      const { data, error, status, statusText } = await supabase
        .from('companies')
        .select('*');
      
      console.log("[DB] getCompanies: Response received", { status, statusText, dataLength: data?.length });

      if (error) {
        console.error("[DB] getCompanies error:", error.message, error.details);
        return [];
      }
      
      if (!data || data.length === 0) {
        console.warn("[DB] getCompanies: No data returned from 'companies' table. This might be an RLS issue or the table is empty.");
        
        // Tenta buscar de uma view alternativa se existir
        console.log("[DB] getCompanies: Trying fallback to 'user_session_data' to see if we can find company info...");
        const { data: sessionData } = await supabase.from('user_session_data').select('company_id, company_name').limit(10);
        if (sessionData && sessionData.length > 0) {
           console.log("[DB] getCompanies: Found some company info in user_session_data", sessionData);
        }
      }
      
      return data ? data.map(mapCompany) : [];
    } catch (e: any) {
      console.error("[DB] getCompanies exception:", e.message);
      return [];
    }
  },

  createCompany: async (company: Company): Promise<Company> => {
    const payload: any = {
      name: company.name,
      status: 'ACTIVE',
      nfse_enabled: company.nfseEnabled,
      max_users: company.maxUsers,
      max_leads: company.maxLeads,
      expires_at: company.expiresAt,
      cnpj: company.cnpj,
      address: company.address,
      city: company.city,
      state: company.state,
      neighborhood: company.neighborhood,
      whatsapp: company.whatsapp,
      contact_email: company.contactEmail,
      manager_name: company.managerName,
      vertical: company.vertical,
      mrr: company.mrr,
      ia_usage_count: company.iaUsageCount,
      equity_value: company.equityValue,
      logo_url: company.logo,
      policy: company.policy
    };
    
    // Suporta tanto 'plan' quanto 'plan_id'
    payload.plan = company.plan;
    payload.plan_id = company.plan;

    const { data, error } = await supabase.from('companies').insert(payload).select().single();
    if (error) {
      console.error("[DB] createCompany error:", error.message);
      // Fallback se colunas novas não existirem
      if (error.message.includes('column')) {
        const simplePayload = {
          name: company.name,
          plan: company.plan,
          status: 'ACTIVE'
        };
        const { data: retryData, error: retryError } = await supabase.from('companies').insert(simplePayload).select().single();
        if (retryError) throw retryError;
        return mapCompany(retryData);
      }
      throw error;
    }
    return mapCompany(data);
  },

  updateCompany: async (id: string, updates: Partial<Company>): Promise<Company> => {
    const payload: any = {};
    if (updates.name) payload.name = updates.name;
    if (updates.plan) {
      payload.plan = updates.plan;
      payload.plan_id = updates.plan;
    }
    if (updates.status) payload.status = updates.status;
    if (updates.nfseEnabled !== undefined) payload.nfse_enabled = updates.nfseEnabled;
    if (updates.blocked !== undefined) payload.blocked = updates.blocked;
    if (updates.maxUsers) payload.max_users = updates.maxUsers;
    if (updates.maxLeads) payload.max_leads = updates.maxLeads;
    if (updates.expiresAt) payload.expires_at = updates.expiresAt;
    if (updates.address) payload.address = updates.address;
    if (updates.cnpj) payload.cnpj = updates.cnpj;
    if (updates.city) payload.city = updates.city;
    if (updates.state) payload.state = updates.state;
    if (updates.neighborhood) payload.neighborhood = updates.neighborhood;
    if (updates.whatsapp) payload.whatsapp = updates.whatsapp;
    if (updates.contactEmail) payload.contact_email = updates.contactEmail;
    if (updates.managerName) payload.manager_name = updates.managerName;
    if (updates.vertical) payload.vertical = updates.vertical;
    if (updates.mrr !== undefined) payload.mrr = updates.mrr;
    if (updates.iaUsageCount !== undefined) payload.ia_usage_count = updates.iaUsageCount;
    if (updates.equityValue !== undefined) payload.equity_value = updates.equityValue;
    if (updates.logoUrl) {
      payload.logo_url = updates.logoUrl;
      payload.logo = updates.logoUrl;
    }
    if (updates.logo) {
      payload.logo = updates.logo;
      payload.logo_url = updates.logo;
    }
    if (updates.policy) payload.policy = updates.policy;
    
    payload.updated_at = new Date().toISOString();

    const { data, error } = await supabase.from('companies').update(payload).eq('id', id).select().single();
    if (error) {
       console.error("[DB] updateCompany error:", error.message);
       throw error;
    }
    return mapCompany(data);
  },

  deleteCompany: async (id: string): Promise<void> => {
    const { error } = await supabase.from('companies').delete().eq('id', id);
    if (error) throw error;
  },

  // Clients
  getClients: async (companyId: string): Promise<Client[]> => {
    if (!hasValidSupabaseConfig) return MOCK_CLIENTS as any;
    const { data, error } = await supabase.from('clients').select('*').eq('company_id', companyId).order('name');
    return error ? [] : data.map(mapClient);
  },

  saveClient: async (client: Client): Promise<Client> => {
    const payload = {
      id: client.id.startsWith('temp-') ? undefined : client.id,
      company_id: client.company_id,
      name: client.name,
      email: client.email,
      phone: client.phone,
      address: client.address,
      document: client.document,
      type: client.type,
      notes: client.notes,
      updated_at: new Date().toISOString()
    };
    const { data, error } = await supabase.from('clients').upsert(payload).select().single();
    if (error) throw error;
    return mapClient(data);
  },

  deleteClient: async (id: string): Promise<void> => {
    const { error } = await supabase.from('clients').delete().eq('id', id);
    if (error) throw error;
  },

  // Leads & Kanban
  getLeads: async (companyId: string): Promise<Lead[]> => {
    if (!hasValidSupabaseConfig) return MOCK_LEADS as any;
    const { data, error } = await supabase.from('leads').select('*').eq('company_id', companyId).order('created_at', { ascending: false });
    return error ? [] : data.map(mapLead);
  },

  saveLead: async (lead: Lead): Promise<Lead> => {
    const payload = {
      id: lead.id.startsWith('temp-') ? undefined : lead.id,
      company_id: lead.companyId,
      name: lead.name,
      email: lead.email,
      phone: lead.phone,
      company: lead.company,
      status: lead.status,
      value: lead.value,
      source: lead.source,
      assigned_to: lead.assignedTo,
      notes: lead.notes || (lead as any).description,
      last_contact: lead.lastContact === 'Agora' ? new Date().toISOString() : lead.lastContact,
      next_follow_up: lead.nextFollowUp || (lead as any).followUpDate,
      tags: lead.tags,
      cep: lead.cep,
      logradouro: lead.logradouro,
      numero: lead.numero,
      bairro: lead.bairro,
      cidade: lead.cidade,
      uf: lead.uf,
      cnpj: lead.cnpj,
      cpf: lead.cpf,
      updated_at: new Date().toISOString()
    };
    const { data, error } = await supabase.from('leads').upsert(payload).select().single();
    if (error) throw error;
    return mapLead(data);
  },

  deleteLead: async (id: string): Promise<void> => {
    const { error } = await supabase.from('leads').delete().eq('id', id);
    if (error) throw error;
  },

  getKanbanColumns: async (companyId: string): Promise<KanbanColumn[]> => {
    const { data, error } = await supabase.from('kanban_columns').select('*').eq('company_id', companyId).order('order');
    return error ? [] : data.map(c => ({
      id: c.id,
      companyId: c.company_id,
      title: c.title,
      order: c.order,
      color: c.color
    }));
  },

  saveKanbanColumn: async (col: KanbanColumn): Promise<KanbanColumn> => {
    const payload = {
      id: col.id.startsWith('temp-') ? undefined : col.id,
      company_id: col.companyId,
      title: col.title,
      order: col.order,
      color: col.color,
      updated_at: new Date().toISOString()
    };
    const { data, error } = await supabase.from('kanban_columns').upsert(payload).select().single();
    if (error) throw error;
    return {
      id: data.id,
      companyId: data.company_id,
      title: data.title,
      order: data.order,
      color: data.color
    };
  },

  saveKanbanColumns: async (companyId: string, columns: KanbanColumn[]): Promise<void> => {
    const payloads = columns.map(col => ({
      id: col.id.startsWith('temp-') ? undefined : col.id,
      company_id: companyId,
      title: col.title,
      order: col.order,
      color: col.color,
      updated_at: new Date().toISOString()
    }));
    const { error } = await supabase.from('kanban_columns').upsert(payloads);
    if (error) throw error;
  },

  deleteKanbanColumn: async (id: string): Promise<void> => {
    const { error } = await supabase.from('kanban_columns').delete().eq('id', id);
    if (error) throw error;
  },

  // Sales (Orders, Estimates, Contracts, Invoices)
  getOrders: async (companyId: string): Promise<Order[]> => {
    if (!hasValidSupabaseConfig) return MOCK_ORDERS as any;
    const { data, error } = await supabase.from('orders').select('*').eq('company_id', companyId).order('created_at', { ascending: false });
    return error ? [] : data.map(mapOrder);
  },

  saveOrder: async (order: Order): Promise<Order> => {
    const payload = {
      id: order.id.startsWith('temp-') ? undefined : order.id,
      company_id: order.company_id,
      client_id: order.clientId,
      team_id: order.teamId,
      nome_evento: order.nomeEvento,
      data_evento: order.dataEvento,
      dias_montagem: order.diasMontagem,
      dia_retirada: order.diaRetirada,
      horario_entrega: order.horarioEntrega,
      horario_retirada: order.horarioRetirada,
      responsavel_interno: order.responsavelInterno,
      status: order.status,
      prioridade: order.prioridade,
      origem: order.origem,
      valor_taxa_deslocamento: order.valorTaxaDeslocamento,
      taxa_applied: order.taxaAplicada,
      motivo_taxa: order.motivoTaxa,
      termo_taxa_aceito: order.termoTaxaAceito,
      termo_taxa_aceito_em: order.termoTaxaAceitoEm,
      materials: order.materials,
      estimated_cost: order.estimatedCost,
      actual_cost: order.actualCost,
      placa: order.placa,
      veiculo_modelo: order.veiculoModelo,
      expenses: order.expenses,
      extra_items: order.extraItems,
      signature: order.signature,
      vendedor_name: order.vendedorName,
      validade_proposta: order.validadeProposta,
      event_photo_url: order.eventPhoto,
      updated_at: new Date().toISOString()
    };
    const { data, error } = await supabase.from('orders').upsert(payload).select().single();
    if (error) throw error;
    return mapOrder(data);
  },

  deleteOrder: async (id: string): Promise<void> => {
    const { error } = await supabase.from('orders').delete().eq('id', id);
    if (error) throw error;
  },

  getEstimates: async (companyId: string): Promise<Estimate[]> => {
    const { data, error } = await supabase.from('estimates').select('*').eq('company_id', companyId).order('created_at', { ascending: false });
    return error ? [] : data.map(mapEstimate);
  },

  saveEstimate: async (estimate: Estimate): Promise<Estimate> => {
    const payload = {
      id: estimate.id.startsWith('temp-') ? undefined : estimate.id,
      company_id: estimate.company_id,
      client_id: estimate.clientId,
      title: estimate.title,
      items: estimate.items,
      total: estimate.total,
      status: estimate.status,
      valid_until: estimate.validUntil,
      notes: estimate.notes,
      delivery_date: estimate.deliveryDate,
      removal_date: estimate.removalDate,
      currency: estimate.currency,
      reference: estimate.reference,
      updated_at: new Date().toISOString()
    };
    const { data, error } = await supabase.from('estimates').upsert(payload).select().single();
    if (error) throw error;
    return mapEstimate(data);
  },

  deleteEstimate: async (id: string): Promise<void> => {
    const { error } = await supabase.from('estimates').delete().eq('id', id);
    if (error) throw error;
  },

  convertEstimateToOrder: async (estimateId: string): Promise<Order> => {
    // 1. Get the estimate
    const { data: estimateData, error: estimateError } = await supabase
      .from('estimates')
      .select('*')
      .eq('id', estimateId)
      .single();
    
    if (estimateError || !estimateData) throw new Error("Orçamento não encontrado.");

    const estimate = mapEstimate(estimateData);

    // 2. Create the order payload
    const orderPayload = {
      company_id: estimate.company_id,
      client_id: estimate.clientId,
      nome_evento: estimate.title,
      data_evento: estimate.deliveryDate || new Date().toISOString(),
      dias_montagem: 1,
      dia_retirada: estimate.removalDate || new Date().toISOString(),
      horario_entrega: '08:00',
      horario_retirada: '18:00',
      responsavel_interno: '',
      status: 'PENDING',
      prioridade: 'MEDIUM',
      origem: 'CRM',
      materials: estimate.items.map(item => ({
        id: item.id,
        name: item.name || item.description,
        qty: item.qty,
        checked: false,
        costPerUnit: item.rate
      })),
      estimated_cost: estimate.total,
      actual_cost: 0,
      estimate_id: estimateId,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };

    // 3. Insert the order
    const { data: orderData, error: orderError } = await supabase
      .from('orders')
      .insert(orderPayload)
      .select()
      .single();
    
    if (orderError) {
      // If estimate_id column doesn't exist, retry without it
      if (orderError.message.includes('estimate_id')) {
        delete (orderPayload as any).estimate_id;
        const { data: retryData, error: retryError } = await supabase
          .from('orders')
          .insert(orderPayload)
          .select()
          .single();
        if (retryError) throw retryError;
        
        // Update estimate status to ACCEPTED
        await supabase
          .from('estimates')
          .update({ status: 'ACEITO', updated_at: new Date().toISOString() })
          .eq('id', estimateId);

        return mapOrder(retryData);
      }
      throw orderError;
    }

    // 4. Update estimate status to ACCEPTED
    await supabase
      .from('estimates')
      .update({ status: 'ACEITO', updated_at: new Date().toISOString() })
      .eq('id', estimateId);

    return mapOrder(orderData);
  },

  getContracts: async (companyId: string): Promise<Contract[]> => {
    const { data, error } = await supabase.from('contracts').select('*').eq('company_id', companyId).order('created_at', { ascending: false });
    return error ? [] : data.map(mapContract);
  },

  saveContract: async (contract: Contract): Promise<Contract> => {
    const payload = {
      id: contract.id.startsWith('temp-') ? undefined : contract.id,
      company_id: contract.company_id,
      client_id: contract.clientId,
      title: contract.title,
      content: contract.content,
      status: contract.status,
      value: contract.value,
      start_date: contract.startDate,
      end_date: contract.endDate,
      signed_at: contract.signedAt,
      updated_at: new Date().toISOString()
    };
    const { data, error } = await supabase.from('contracts').upsert(payload).select().single();
    if (error) throw error;
    return mapContract(data);
  },

  deleteContract: async (id: string): Promise<void> => {
    const { error } = await supabase.from('contracts').delete().eq('id', id);
    if (error) throw error;
  },

  getInvoices: async (companyId: string): Promise<Invoice[]> => {
    const { data, error } = await supabase.from('invoices').select('*').eq('company_id', companyId).order('created_at', { ascending: false });
    return error ? [] : data.map(mapInvoice);
  },

  saveInvoice: async (invoice: Invoice): Promise<Invoice> => {
    const payload = {
      id: invoice.id.startsWith('temp-') ? undefined : invoice.id,
      company_id: invoice.company_id,
      client_id: invoice.clientId,
      number: invoice.number,
      client_name: invoice.clientName,
      date: invoice.date,
      due_date: invoice.dueDate,
      status: invoice.status,
      total: invoice.total,
      items: invoice.items,
      updated_at: new Date().toISOString()
    };
    const { data, error } = await supabase.from('invoices').upsert(payload).select().single();
    if (error) throw error;
    return mapInvoice(data);
  },

  deleteInvoice: async (id: string): Promise<void> => {
    const { error } = await supabase.from('invoices').delete().eq('id', id);
    if (error) throw error;
  },

  // Products & Teams
  getProducts: async (companyId: string): Promise<Product[]> => {
    const { data, error } = await supabase.from('products').select('*').eq('company_id', companyId).order('name');
    return error ? [] : data.map(p => ({
      id: p.id,
      company_id: p.company_id,
      name: p.name,
      description: p.description,
      price: Number(p.price),
      category: p.category,
      stock: Number(p.stock),
      unit: p.unit
    }));
  },

  saveProduct: async (product: Product): Promise<Product> => {
    const payload = {
      id: product.id.startsWith('temp-') ? undefined : product.id,
      company_id: product.company_id,
      name: product.name,
      description: product.description,
      price: product.price,
      category: product.category,
      stock: product.stock,
      unit: product.unit,
      updated_at: new Date().toISOString()
    };
    const { data, error } = await supabase.from('products').upsert(payload).select().single();
    if (error) throw error;
    return {
      id: data.id,
      company_id: data.company_id,
      name: data.name,
      description: data.description,
      price: Number(data.price),
      category: data.category,
      stock: Number(data.stock),
      unit: data.unit
    };
  },

  deleteProduct: async (id: string): Promise<void> => {
    const { error } = await supabase.from('products').delete().eq('id', id);
    if (error) throw error;
  },

  getTeams: async (companyId: string): Promise<Team[]> => {
    const { data, error } = await supabase.from('teams').select('*').eq('company_id', companyId);
    return error ? [] : (data as any[]).map(t => ({
      id: t.id,
      company_id: t.company_id,
      name: t.name,
      members: t.members || [],
      leaderId: t.leader_id
    }));
  },

  saveTeam: async (team: Team): Promise<Team> => {
    const payload = {
      id: team.id.startsWith('temp-') ? undefined : team.id,
      company_id: team.company_id,
      name: team.name,
      members: team.members,
      leader_id: team.leaderId,
      updated_at: new Date().toISOString()
    };
    const { data, error } = await supabase.from('teams').upsert(payload).select().single();
    if (error) throw error;
    return {
      id: data.id,
      company_id: data.company_id,
      name: data.name,
      members: data.members || [],
      leaderId: data.leader_id
    };
  },

  deleteTeam: async (id: string): Promise<void> => {
    const { error } = await supabase.from('teams').delete().eq('id', id);
    if (error) throw error;
  },

  // Finance
  getPayments: async (companyId: string): Promise<any[]> => {
    const { data, error } = await supabase.from('payments').select('*').eq('company_id', companyId).order('date', { ascending: false });
    return error ? [] : data.map((p: any) => ({
      id: p.id,
      company_id: p.company_id,
      paymentNum: p.payment_num,
      invoiceNum: p.invoice_num,
      method: p.method,
      transactionId: p.transaction_id,
      clientName: p.client_name,
      amount: Number(p.amount),
      date: p.date,
      status: p.status,
      description: p.description,
      createdAt: p.created_at
    }));
  },

  savePayment: async (payment: any): Promise<void> => {
    const payload = {
      id: payment.id,
      company_id: payment.company_id,
      payment_num: payment.paymentNum,
      invoice_num: payment.invoiceNum,
      method: payment.method,
      transaction_id: payment.transactionId,
      client_name: payment.clientName,
      amount: payment.amount,
      date: payment.date,
      status: payment.status,
      description: payment.description,
      created_at: payment.createdAt || new Date().toISOString(),
      updated_at: new Date().toISOString()
    };
    const { error } = await supabase.from('payments').upsert(payload);
    if (error) throw error;
  },

  deletePayment: async (id: string): Promise<void> => {
    const { error } = await supabase.from('payments').delete().eq('id', id);
    if (error) throw error;
  },

  getDriverPayments: async (companyId: string): Promise<DriverPayment[]> => {
    const { data, error } = await supabase.from('driver_payments').select('*').eq('company_id', companyId).order('created_at', { ascending: false });
    return error ? [] : data.map(mapDriverPayment);
  },

  saveDriverPayment: async (payment: DriverPayment): Promise<DriverPayment> => {
    const payload = {
      id: payment.id.startsWith('audit-') || payment.id.startsWith('temp-') ? undefined : payment.id,
      company_id: payment.company_id,
      user_id: payment.userId,
      type: payment.type,
      amount: payment.amount,
      date: payment.date,
      description: payment.description,
      status: payment.status,
      updated_at: new Date().toISOString()
    };
    const { data, error } = await supabase.from('driver_payments').upsert(payload).select().single();
    if (error) throw error;
    return mapDriverPayment(data);
  },

  deleteDriverPayment: async (id: string): Promise<void> => {
    const { error } = await supabase.from('driver_payments').delete().eq('id', id);
    if (error) throw error;
  },

  getFixedExpenses: async (companyId: string): Promise<any[]> => {
    const { data, error } = await supabase.from('fixed_expenses').select('*').eq('company_id', companyId);
    return error ? [] : data.map((e: any) => ({
      id: e.id,
      company_id: e.company_id,
      description: e.description,
      amount: Number(e.amount),
      dueDay: e.due_day,
      category: e.category,
      startDate: e.start_date,
      endDate: e.end_date,
      status: e.status,
      createdAt: e.created_at
    }));
  },

  saveFixedExpense: async (expense: any): Promise<void> => {
    const payload = {
      id: expense.id,
      company_id: expense.company_id,
      description: expense.description,
      amount: expense.amount,
      due_day: expense.dueDay,
      category: expense.category,
      start_date: expense.startDate,
      end_date: expense.endDate,
      status: expense.status,
      created_at: expense.createdAt,
      updated_at: new Date().toISOString()
    };
    const { error } = await supabase.from('fixed_expenses').upsert(payload);
    if (error) throw error;
  },

  deleteFixedExpense: async (id: string): Promise<void> => {
    const { error } = await supabase.from('fixed_expenses').delete().eq('id', id);
    if (error) throw error;
  },

  getSaasTransactions: async (companyId: string): Promise<SaasTransaction[]> => {
    const { data, error } = await supabase.from('saas_transactions').select('*').eq('company_id', companyId).order('date', { ascending: false });
    return error ? [] : data.map(t => ({
      id: t.id,
      companyId: t.company_id,
      amount: Number(t.amount),
      type: t.type,
      status: t.status,
      date: t.date
    }));
  },

  // System & Projects
  getPlans: async (): Promise<Plan[]> => {
    if (!hasValidSupabaseConfig) return MOCK_PLANS as any;
    const { data, error } = await supabase.from('plans').select('*').order('price');
    return error ? [] : data.map(p => ({
      id: p.id,
      name: p.name,
      subtitle: p.subtitle || '',
      price: Number(p.price),
      setup: Number(p.setup) || 0,
      features: p.features || [],
      popular: p.popular || false,
      btnColor: p.btn_color || 'bg-slate-100 text-slate-800',
      maxItems: p.max_items || 100,
      maxUsers: p.max_users || 5,
      maxLeads: p.max_leads || 100
    }));
  },

  updatePlan: async (id: string, updates: Partial<Plan>): Promise<Plan> => {
    const { data, error } = await supabase.from('plans').update(updates).eq('id', id).select().single();
    if (error) throw error;
    return data;
  },

  getSystemConfig: async (key: string): Promise<any> => {
    try {
        const { data, error } = await supabase.from('system_settings').select('value').eq('key', key).single();
        if (!error && data) return data.value;
    } catch(e) {
        console.error("Error fetching system config:", e);
    }
    const local = localStorage.getItem(`evl_cfg_${key}`);
    return local ? JSON.parse(local) : null;
  },

  updateSystemConfig: async (key: string, value: any): Promise<void> => {
    localStorage.setItem(`evl_cfg_${key}`, JSON.stringify(value));
    await supabase.from('system_settings').upsert({ key, value, updated_at: new Date().toISOString() }, { onConflict: 'key' });
  },

  getProjects: async (companyId: string): Promise<Project[]> => {
    const { data, error } = await supabase.from('projects').select('*').eq('company_id', companyId);
    return error ? [] : data.map(mapProject);
  },

  saveProject: async (project: Project, companyId: string): Promise<Project> => {
    const payload = {
      id: project.id.startsWith('temp-') ? undefined : project.id,
      company_id: companyId,
      name: project.name,
      client_id: project.clientId,
      status: project.status,
      progress: project.progress,
      calculate_progress_by_tasks: project.calculateProgressByTasks,
      billing_type: project.billingType,
      estimated_hours: project.estimatedHours,
      start_date: project.startDate,
      deadline: project.deadline,
      tags: project.tags,
      members: project.members,
      description: project.description,
      send_email_on_create: project.sendEmailOnCreate,
      contact_notification_type: project.contactNotificationType,
      visible_tabs: project.visibleTabs,
      permissions: project.permissions,
      updated_at: new Date().toISOString()
    };
    const { data, error } = await supabase.from('projects').upsert(payload).select().single();
    if (error) throw error;
    return mapProject(data);
  },

  deleteProject: async (id: string): Promise<void> => {
    const { error } = await supabase.from('projects').delete().eq('id', id);
    if (error) throw error;
  },

  getTasks: async (companyId: string, projectId?: string): Promise<Task[]> => {
    let query = supabase.from('tasks').select('*').eq('company_id', companyId);
    if (projectId) query = query.eq('project_id', projectId);
    const { data, error } = await query.order('created_at', { ascending: false });
    return error ? [] : data.map(mapTask);
  },

  saveTask: async (task: Task): Promise<Task> => {
    const payload = {
      id: task.id.startsWith('temp-') ? undefined : task.id,
      company_id: task.company_id,
      project_id: task.projectId,
      name: task.name,
      description: task.description,
      status: task.status,
      priority: task.priority,
      start_date: task.startDate,
      deadline: task.deadline,
      assigned_to: task.assignedTo,
      tags: task.tags,
      updated_at: new Date().toISOString()
    };
    const { data, error } = await supabase.from('tasks').upsert(payload).select().single();
    if (error) throw error;
    return mapTask(data);
  },

  deleteTask: async (id: string): Promise<void> => {
    const { error } = await supabase.from('tasks').delete().eq('id', id);
    if (error) throw error;
  },

  sendWelcomeEmail: async (company: Company, user: User, type: 'STAFF' | 'MANAGER'): Promise<void> => {
    try {
      await supabase.functions.invoke('send-test-email', {
        body: {
          to: user.email,
          subject: `Bem-vindo ao EventLog Pro - ${company.name}`,
          template: type === 'STAFF' ? 'welcome_staff' : 'welcome_manager',
          data: { 
            name: user.name, 
            role: user.role, 
            companyName: company.name,
            loginUrl: window.location.origin 
          }
        }
      });
    } catch (e) {
      console.error("[MAILER ERROR] sendWelcomeEmail failed:", e);
      throw e;
    }
  }
};
